import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, Send, Search, Bell, BellOff, ArrowLeft, Users } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

const MessagesSection = ({ token }) => {
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/conversations`, { headers });
      setConversations(res.data);
    } catch {} finally { setLoading(false); }
  }, [token]);

  const fetchMessages = useCallback(async (tenantId) => {
    try {
      const res = await axios.get(`${API}/chat/conversations/${tenantId}/messages`, { headers });
      setMessages(res.data);
      // Refresh conversations to reset unread
      fetchConversations();
    } catch {}
  }, [token, fetchConversations]);

  useEffect(() => {
    fetchConversations();
    // Poll
    pollRef.current = setInterval(() => {
      fetchConversations();
      if (selectedConv) fetchMessages(selectedConv.tenant_id);
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, []);

  // Re-register poll when selectedConv changes
  useEffect(() => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      fetchConversations();
      if (selectedConv) fetchMessages(selectedConv.tenant_id);
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [selectedConv, fetchConversations, fetchMessages]);

  useEffect(() => {
    if (selectedConv) fetchMessages(selectedConv.tenant_id);
  }, [selectedConv]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Push subscription check
  useEffect(() => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      navigator.serviceWorker.getRegistration('/sw.js').then(reg => {
        if (reg) reg.pushManager.getSubscription().then(sub => setPushEnabled(!!sub));
      });
    }
  }, []);

  const registerPush = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    setPushLoading(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setPushLoading(false); return; }
      let reg = await navigator.serviceWorker.getRegistration('/sw.js');
      if (!reg) reg = await navigator.serviceWorker.register('/sw.js');
      const { data } = await axios.get(`${API}/chat/vapid-public-key`);
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key)
      });
      await axios.post(`${API}/chat/push-subscribe`, {
        subscription: sub.toJSON(), role: 'ultra_admin'
      }, { headers });
      setPushEnabled(true);
    } catch (e) { console.error('Push register:', e); }
    setPushLoading(false);
  };

  const unregisterPush = async () => {
    setPushLoading(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration('/sw.js');
      if (reg) { const sub = await reg.pushManager.getSubscription(); if (sub) await sub.unsubscribe(); }
      setPushEnabled(false);
    } catch {}
    setPushLoading(false);
  };

  const sendMessage = async () => {
    if (!input.trim() || sending || !selectedConv) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat/conversations/${selectedConv.tenant_id}/messages`, { content: input.trim() }, { headers });
      setMessages(prev => [...prev, res.data]);
      setInput('');
      fetchConversations();
    } catch {}
    setSending(false);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' }) + ' ' + d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  };

  const totalUnread = conversations.reduce((s, c) => s + (c.unread || 0), 0);
  const filtered = conversations.filter(c =>
    c.tenant_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.user_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.user_email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="flex h-full" style={{ minHeight: '600px' }}>
      {/* Left: Conversations list */}
      <div className={`flex flex-col border-r border-gray-100 ${selectedConv ? 'hidden lg:flex' : 'flex'} w-full lg:w-80 flex-shrink-0`}>
        {/* Header */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-gray-800">Mesajlar</h2>
              {totalUnread > 0 && (
                <span className="bg-purple-600 text-white text-xs px-2 py-0.5 rounded-full font-bold">{totalUnread}</span>
              )}
            </div>
            <button
              onClick={pushEnabled ? unregisterPush : registerPush}
              disabled={pushLoading}
              className="p-2 rounded-xl hover:bg-gray-100 transition-colors"
              title={pushEnabled ? 'Bildirimleri kapat' : 'Bildirimleri aç'}
            >
              {pushEnabled
                ? <Bell className="w-5 h-5 text-yellow-500" />
                : <BellOff className="w-5 h-5 text-gray-400" />
              }
            </button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Müşteri ara..."
              className="w-full pl-9 pr-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-purple-400"
            />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="p-6 text-center text-gray-400 text-sm">Yükleniyor...</div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="p-8 text-center">
              <Users className="w-12 h-12 text-gray-200 mx-auto mb-3" />
              <p className="text-gray-400 text-sm">Henüz mesaj yok</p>
            </div>
          )}
          {filtered.map(conv => (
            <button
              key={conv.tenant_id}
              data-testid={`conv-${conv.tenant_id}`}
              onClick={() => setSelectedConv(conv)}
              className={`w-full px-4 py-3 flex items-start gap-3 hover:bg-gray-50 transition-colors text-left border-b border-gray-50 ${selectedConv?.tenant_id === conv.tenant_id ? 'bg-purple-50' : ''}`}
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold"
                style={{ background: 'linear-gradient(135deg, #6d28d9, #4f46e5)' }}>
                {(conv.tenant_name || 'M')[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm text-gray-800 truncate">{conv.tenant_name}</span>
                  {conv.last_at && <span className="text-xs text-gray-400 flex-shrink-0 ml-1">{formatTime(conv.last_at)}</span>}
                </div>
                <p className="text-xs text-gray-500 truncate mt-0.5">{conv.user_name || conv.user_email}</p>
                <p className="text-xs text-gray-400 truncate mt-0.5">{conv.last_message}</p>
              </div>
              {conv.unread > 0 && (
                <span className="bg-purple-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold flex-shrink-0 mt-1">
                  {conv.unread}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Right: Chat window */}
      <div className={`flex-1 flex flex-col ${selectedConv ? 'flex' : 'hidden lg:flex'}`}>
        {!selectedConv ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <MessageCircle className="w-16 h-16 text-gray-200 mb-4" />
            <p className="text-gray-500 font-medium">Bir konuşma seçin</p>
            <p className="text-gray-400 text-sm mt-1">Sol listeden müşteri seçerek sohbete başlayın</p>
          </div>
        ) : (
          <>
            {/* Chat header */}
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-3 bg-white">
              <button onClick={() => setSelectedConv(null)} className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100">
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold"
                style={{ background: 'linear-gradient(135deg, #6d28d9, #4f46e5)' }}>
                {(selectedConv.tenant_name || 'M')[0].toUpperCase()}
              </div>
              <div>
                <p className="font-semibold text-gray-800 text-sm">{selectedConv.tenant_name}</p>
                <p className="text-xs text-gray-400">{selectedConv.user_email}</p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2" style={{ background: '#f8fafc' }}>
              {messages.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full">
                  <MessageCircle className="w-10 h-10 text-gray-300 mb-2" />
                  <p className="text-gray-400 text-sm">Henüz mesaj yok. İlk mesajı gönderin!</p>
                </div>
              )}
              {messages.map(msg => (
                <div key={msg.id} className={`flex ${msg.sender_role === 'ultra_admin' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[70%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
                      msg.sender_role === 'ultra_admin'
                        ? 'text-white rounded-tr-sm'
                        : 'bg-white text-gray-800 rounded-tl-sm border border-gray-100'
                    }`}
                    style={msg.sender_role === 'ultra_admin' ? { background: 'linear-gradient(135deg, #6d28d9, #4f46e5)' } : {}}
                  >
                    {msg.sender_role !== 'ultra_admin' && (
                      <p className="text-xs font-semibold text-purple-600 mb-0.5">{msg.sender_name}</p>
                    )}
                    <p style={{ wordBreak: 'break-word' }}>{msg.content}</p>
                    <p className={`text-[10px] mt-0.5 text-right ${msg.sender_role === 'ultra_admin' ? 'text-white/60' : 'text-gray-400'}`}>
                      {formatTime(msg.created_at)}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <div className="p-3 border-t border-gray-100 bg-white">
              <div className="flex items-end gap-2">
                <textarea
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKey}
                  placeholder="Mesaj yaz... (Enter = Gönder)"
                  rows={2}
                  className="flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-200"
                />
                <button
                  onClick={sendMessage}
                  disabled={sending || !input.trim()}
                  className="w-10 h-10 rounded-xl flex items-center justify-center transition-all disabled:opacity-40"
                  style={{ background: 'linear-gradient(135deg, #6d28d9, #4f46e5)' }}
                >
                  <Send className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MessagesSection;
